/**
 * Octo bildirim sesleri — Web Audio ile programda üretilir (ses dosyası yok, her cihazda aynı).
 * Kullanıcı mesaj ve bildirim için ayrı ses seçer (Ayarlar › Bildirimler); ses düzeyi ortak.
 *
 * Tarayıcılar kullanıcı etkileşiminden önce sesi engelleyebilir: ilk tıklamada ses bağlamı
 * "uyandırılır" (unlock), engellenen ses sessizce atlanır.
 */
export const OCTO_SOUNDS = ['octo', 'drop', 'pop', 'chime', 'bell', 'soft', 'none'] as const;
export type OctoSound = (typeof OCTO_SOUNDS)[number];

type Note = { f: number; t: number; d: number; type?: OscillatorType; g?: number; slideTo?: number };

/** Her ses: notalar (frekans Hz, başlangıç s, süre s). */
const PATTERNS: Record<Exclude<OctoSound, 'none'>, Note[]> = {
  // Octo imzası: yükselen iki nota, yumuşak çan tınısı.
  octo: [
    { f: 784, t: 0, d: 0.16, type: 'sine', g: 0.9 },
    { f: 1175, t: 0.11, d: 0.32, type: 'sine', g: 0.8 },
    { f: 2350, t: 0.11, d: 0.22, type: 'sine', g: 0.12 },
  ],
  // Su damlası: hızlı aşağı kayan ton.
  drop: [{ f: 1400, t: 0, d: 0.18, type: 'sine', g: 0.9, slideTo: 520 }],
  // Kısa "pop".
  pop: [{ f: 660, t: 0, d: 0.09, type: 'triangle', g: 1, slideTo: 990 }],
  // Üç notalı zil.
  chime: [
    { f: 1047, t: 0, d: 0.22, type: 'sine', g: 0.7 },
    { f: 1319, t: 0.09, d: 0.22, type: 'sine', g: 0.6 },
    { f: 1568, t: 0.18, d: 0.34, type: 'sine', g: 0.6 },
  ],
  // Çan: temel + harmonikler, uzun sönüm.
  bell: [
    { f: 880, t: 0, d: 0.9, type: 'sine', g: 0.8 },
    { f: 1760, t: 0, d: 0.6, type: 'sine', g: 0.25 },
    { f: 2640, t: 0, d: 0.35, type: 'sine', g: 0.12 },
  ],
  // Yumuşak, alçak iki nota (sessiz ortamlar için).
  soft: [
    { f: 523, t: 0, d: 0.25, type: 'sine', g: 0.6 },
    { f: 659, t: 0.15, d: 0.35, type: 'sine', g: 0.5 },
  ],
};

let ctx: AudioContext | null = null;
function audio(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  ctx ??= new Ctor();
  return ctx;
}

/** İlk kullanıcı etkileşiminde ses bağlamını açar (tarayıcı otomatik çalma kuralı). */
export function unlockSounds() {
  const a = audio();
  if (a && a.state === 'suspended') void a.resume().catch(() => {});
}
if (typeof window !== 'undefined') {
  const once = () => {
    unlockSounds();
    window.removeEventListener('pointerdown', once);
    window.removeEventListener('keydown', once);
  };
  window.addEventListener('pointerdown', once);
  window.addEventListener('keydown', once);
}

let lastPlayed = 0;

/** Sesi çalar. `volume` 0–1. Art arda gelen olaylarda (ör. 5 mesaj) 600 ms içinde tekrar çalmaz. */
export function playOctoSound(sound: OctoSound, volume = 0.6, opts: { force?: boolean } = {}) {
  if (sound === 'none' || volume <= 0) return;
  const now = Date.now();
  if (!opts.force && now - lastPlayed < 600) return;
  lastPlayed = now;
  const a = audio();
  if (!a) return;
  if (a.state === 'suspended') void a.resume().catch(() => {});
  const master = a.createGain();
  master.gain.value = Math.min(1, Math.max(0, volume)) * 0.35;
  master.connect(a.destination);
  const start = a.currentTime + 0.01;
  for (const n of PATTERNS[sound]) {
    const osc = a.createOscillator();
    const g = a.createGain();
    osc.type = n.type ?? 'sine';
    osc.frequency.setValueAtTime(n.f, start + n.t);
    if (n.slideTo) osc.frequency.exponentialRampToValueAtTime(n.slideTo, start + n.t + n.d);
    const peak = n.g ?? 0.8;
    g.gain.setValueAtTime(0.0001, start + n.t);
    g.gain.exponentialRampToValueAtTime(peak, start + n.t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, start + n.t + n.d);
    osc.connect(g).connect(master);
    osc.start(start + n.t);
    osc.stop(start + n.t + n.d + 0.05);
  }
}
